import React, { useEffect, useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FaDownload, FaPrint, FaRedo, FaShoppingBag, FaPlus, FaTrash } from 'react-icons/fa';
import SEO from '../components/SEO';

const PRESETS = {
  grocery: {
    label: 'Grocery Bill',
    title: 'GROCERY BILL',
    shopName: 'Fresh Mart Grocery',
    shopTagline: 'Daily essentials and household provisions',
    defaultTaxRate: 0,
    defaultItems: [
      { name: 'Rice (5 kg)', qty: 1, rate: 320 },
      { name: 'Cooking Oil (1 L)', qty: 2, rate: 160 },
      { name: 'Milk (1 L)', qty: 3, rate: 60 },
      { name: 'Bread', qty: 2, rate: 35 },
    ],
  },
  kirana: {
    label: 'Kirana Store Bill',
    title: 'KIRANA BILL',
    shopName: 'Neighborhood Kirana Store',
    shopTagline: 'Everyday household essentials',
    defaultTaxRate: 0,
    defaultItems: [
      { name: 'Aata (10 kg)', qty: 1, rate: 450 },
      { name: 'Sugar (5 kg)', qty: 1, rate: 260 },
      { name: 'Tea Powder (500 g)', qty: 2, rate: 180 },
      { name: 'Soap Pack', qty: 1, rate: 150 },
    ],
  },
  shop: {
    label: 'Any Shop Bill',
    title: 'SHOP BILL',
    shopName: 'General Store',
    shopTagline: 'Retail and mixed goods billing',
    defaultTaxRate: 0,
    defaultItems: [
      { name: 'Product A', qty: 1, rate: 250 },
      { name: 'Product B', qty: 2, rate: 180 },
      { name: 'Product C', qty: 1, rate: 120 },
    ],
  },
  retail: {
    label: 'Retail Invoice',
    title: 'RETAIL INVOICE',
    shopName: 'Retail Corner',
    shopTagline: 'General merchandise and home goods',
    defaultTaxRate: 5,
    defaultItems: [
      { name: 'Detergent', qty: 2, rate: 210 },
      { name: 'Lunch Box', qty: 1, rate: 340 },
      { name: 'Water Bottle', qty: 2, rate: 120 },
      { name: 'Notebook Set', qty: 3, rate: 85 },
    ],
  },
  bakery: {
    label: 'Bakery Bill',
    title: 'BAKERY BILL',
    shopName: 'City Bakery',
    shopTagline: 'Snacks, breads, and sweet treats',
    defaultTaxRate: 0,
    defaultItems: [
      { name: 'Bread Loaf', qty: 2, rate: 40 },
      { name: 'Cream Cake Slice', qty: 4, rate: 55 },
      { name: 'Cookies Pack', qty: 2, rate: 90 },
      { name: 'Puffs', qty: 6, rate: 25 },
    ],
  },
  wholesale: {
    label: 'Wholesale Bill',
    title: 'WHOLESALE BILL',
    shopName: 'Wholesale Mart',
    shopTagline: 'Bulk order billing for shops and distributors',
    defaultTaxRate: 12,
    defaultItems: [
      { name: 'Rice Bag (25 kg)', qty: 4, rate: 1420 },
      { name: 'Cooking Oil Tin', qty: 6, rate: 930 },
      { name: 'Detergent Carton', qty: 3, rate: 1650 },
      { name: 'Spice Pack', qty: 5, rate: 410 },
    ],
  },
};

const newItem = (name = '', qty = 1, rate = 0) => ({
  id: Math.random().toString(36).slice(2, 9),
  name,
  qty,
  rate,
});

const formatMoney = (amount) =>
  Number(amount || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const today = () => new Date().toLocaleDateString('en-GB');

const ShopBillGenerator = () => {
  const [preset, setPreset] = useState('grocery');
  const [storeName, setStoreName] = useState(PRESETS.grocery.shopName);
  const [storeTagline, setStoreTagline] = useState(PRESETS.grocery.shopTagline);
  const [invoiceNo, setInvoiceNo] = useState(`SB-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`);
  const [billDate, setBillDate] = useState(today());
  const [customerName, setCustomerName] = useState('Customer Name');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [taxRate, setTaxRate] = useState(PRESETS.grocery.defaultTaxRate);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [items, setItems] = useState(PRESETS.grocery.defaultItems.map((item) => newItem(item.name, item.qty, item.rate)));

  useEffect(() => {
    const config = PRESETS[preset];
    setStoreName(config.shopName);
    setStoreTagline(config.shopTagline);
    setTaxRate(config.defaultTaxRate);
    setItems(config.defaultItems.map((item) => newItem(item.name, item.qty, item.rate)));
  }, [preset]);

  const computed = useMemo(() => {
    const lines = items.map((item) => {
      const qty = Number(item.qty) || 0;
      const rate = Number(item.rate) || 0;
      const amount = qty * rate;
      return { ...item, qty, rate, amount };
    });

    const subtotal = lines.reduce((sum, item) => sum + item.amount, 0);
    const discount = (subtotal * (Number(discountPercent) || 0)) / 100;
    const taxable = Math.max(0, subtotal - discount);
    const tax = (taxable * (Number(taxRate) || 0)) / 100;
    const grandTotal = taxable + tax;

    return {
      lines,
      subtotal,
      discount,
      taxable,
      tax,
      grandTotal,
    };
  }, [items, discountPercent, taxRate]);

  const addItem = () => setItems((prev) => [...prev, newItem()]);
  const removeItem = (id) => setItems((prev) => prev.filter((item) => item.id !== id));
  const updateItem = (id, field, value) => setItems((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)));

  const handleReset = () => {
    const config = PRESETS[preset];
    setStoreName(config.shopName);
    setStoreTagline(config.shopTagline);
    setInvoiceNo(`SB-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`);
    setBillDate(today());
    setCustomerName('Customer Name');
    setPaymentMode('Cash');
    setTaxRate(config.defaultTaxRate);
    setDiscountPercent(0);
    setItems(config.defaultItems.map((item) => newItem(item.name, item.qty, item.rate)));
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(storeName || PRESETS[preset].shopName, 105, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(storeTagline || PRESETS[preset].shopTagline, 105, 22, { align: 'center' });
    doc.text(`${PRESETS[preset].label} | Bill No: ${invoiceNo}`, 105, 28, { align: 'center' });
    doc.text(`Date: ${billDate} | Customer: ${customerName} | Payment: ${paymentMode}`, 105, 34, { align: 'center' });

    autoTable(doc, {
      startY: 42,
      head: [['Item', 'Qty', 'Rate', 'Amount']],
      body: computed.lines.map((item) => [item.name || '-', String(item.qty || 0), formatMoney(item.rate), formatMoney(item.amount)]),
      styles: { fontSize: 10 },
      headStyles: { fillColor: [37, 99, 235] },
    });

    let y = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.text(`Subtotal: Rs. ${formatMoney(computed.subtotal)}`, 14, y);
    y += 6;
    doc.text(`Discount: Rs. ${formatMoney(computed.discount)}`, 14, y);
    y += 6;
    doc.text(`Tax (${taxRate}%): Rs. ${formatMoney(computed.tax)}`, 14, y);
    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.text(`Grand Total: Rs. ${formatMoney(computed.grandTotal)}`, 14, y);

    doc.save(`${preset === 'grocery' ? 'grocery' : 'shop'}-bill.pdf`);
  };

  const billTitle = PRESETS[preset].title;

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="Shop Bill Generator"
        description="Free grocery bill and any shop bill generator with itemized totals, discount, tax, and PDF export."
        keywords="grocery bill generator, shop bill generator, retail bill generator, itemized bill maker, shop invoice generator"
        canonicalPath="/shop-bill"
        calculatorType="Shop Bill Generator"
        calculatorDescription="Generate grocery or shop bills with editable items, discount, tax, and printable PDF output."
        faqItems={[
          {
            question: 'What can I create with this bill generator?',
            answer: 'You can create grocery bills and general shop bills with itemized quantities, rates, tax, discount, and total amount.',
          },
          {
            question: 'Can I use it for small retail stores?',
            answer: 'Yes. It is suitable for kirana stores, retail counters, provision shops, and similar small business billing needs.',
          },
          {
            question: 'Can I print or download the bill?',
            answer: 'Yes. The page includes PDF export so you can save or print the generated bill.',
          },
          {
            question: 'Does it support tax or discount?',
            answer: 'Yes. You can add a flat discount percentage and a tax percentage to the bill total.',
          },
        ]}
      />

      <div className="calculator-header">
        <h1><FaShoppingBag style={{ color: '#2563eb' }} /> Shop Bill Generator</h1>
        <p>Create grocery bills and any shop bills with itemized totals, discount, tax, and PDF export.</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Bill Template</label>
            <div className="tabs" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <button className={`tab ${preset === 'grocery' ? 'active' : ''}`} onClick={() => setPreset('grocery')}>
                Grocery Bill
              </button>
              <button className={`tab ${preset === 'kirana' ? 'active' : ''}`} onClick={() => setPreset('kirana')}>
                Kirana Bill
              </button>
              <button className={`tab ${preset === 'shop' ? 'active' : ''}`} onClick={() => setPreset('shop')}>
                Any Shop Bill
              </button>
              <button className={`tab ${preset === 'retail' ? 'active' : ''}`} onClick={() => setPreset('retail')}>
                Retail Invoice
              </button>
              <button className={`tab ${preset === 'bakery' ? 'active' : ''}`} onClick={() => setPreset('bakery')}>
                Bakery Bill
              </button>
              <button className={`tab ${preset === 'wholesale' ? 'active' : ''}`} onClick={() => setPreset('wholesale')}>
                Wholesale Bill
              </button>
            </div>
          </div>

          <div className="input-group">
            <label>Shop Name</label>
            <input value={storeName} onChange={(e) => setStoreName(e.target.value)} />
          </div>

          <div className="input-group">
            <label>Shop Tagline</label>
            <input value={storeTagline} onChange={(e) => setStoreTagline(e.target.value)} />
          </div>

          <div className="input-group">
            <label>Bill No.</label>
            <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
          </div>

          <div className="input-group">
            <label>Date</label>
            <input value={billDate} onChange={(e) => setBillDate(e.target.value)} />
          </div>

          <div className="input-group">
            <label>Customer Name</label>
            <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
          </div>

          <div className="input-group">
            <label>Payment Mode</label>
            <input value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} />
          </div>

          <div className="input-group">
            <label>Discount (%)</label>
            <input type="number" value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value === '' ? '' : Number(e.target.value))} min="0" step="0.1" />
          </div>

          <div className="input-group">
            <label>Tax (%)</label>
            <input type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value === '' ? '' : Number(e.target.value))} min="0" step="0.1" />
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          <div className="result-summary" style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)' }}>
            <h3>{billTitle}</h3>
            <div className="amount">Rs. {formatMoney(computed.grandTotal)}</div>
            <small>{computed.lines.length} items | {paymentMode}</small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
            <div className="result-item" style={{ background: '#eff6ff', textAlign: 'center', padding: '16px' }}>
              <span className="label">Subtotal</span>
              <span className="value" style={{ color: '#2563eb' }}>Rs. {formatMoney(computed.subtotal)}</span>
            </div>
            <div className="result-item" style={{ background: '#f0fdf4', textAlign: 'center', padding: '16px' }}>
              <span className="label">Grand Total</span>
              <span className="value" style={{ color: '#16a34a' }}>Rs. {formatMoney(computed.grandTotal)}</span>
            </div>
          </div>

          <div className="breakup-section">
            <h4>Item List</h4>
            <table className="tax-slab-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {computed.lines.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <input value={item.name} onChange={(e) => updateItem(item.id, 'name', e.target.value)} style={{ width: '100%' }} />
                    </td>
                    <td>
                      <input type="number" value={item.qty} onChange={(e) => updateItem(item.id, 'qty', e.target.value === '' ? '' : Number(e.target.value))} min="0" style={{ width: '90px' }} />
                    </td>
                    <td>
                      <input type="number" value={item.rate} onChange={(e) => updateItem(item.id, 'rate', e.target.value === '' ? '' : Number(e.target.value))} min="0" step="0.01" style={{ width: '100px' }} />
                    </td>
                    <td>{formatMoney(item.amount)}</td>
                    <td>
                      <button className="btn btn-outline" onClick={() => removeItem(item.id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="btn btn-outline" style={{ marginTop: '12px' }} onClick={addItem}>
              <FaPlus style={{ marginRight: '8px' }} /> Add Item
            </button>
          </div>

          <div className="result-details" style={{ marginTop: '20px' }}>
            <div className="result-item">
              <span className="label">Discount</span>
              <span className="value">Rs. {formatMoney(computed.discount)}</span>
            </div>
            <div className="result-item">
              <span className="label">Tax</span>
              <span className="value">Rs. {formatMoney(computed.tax)}</span>
            </div>
            <div className="result-item">
              <span className="label">Round Off</span>
              <span className="value">Rs. {formatMoney(computed.grandTotal - computed.taxable)}</span>
            </div>
            <div className="result-item" style={{ background: '#fef3c7' }}>
              <span className="label">Amount Payable</span>
              <span className="value">Rs. {formatMoney(computed.grandTotal)}</span>
            </div>
          </div>

          <button className="btn btn-outline" style={{ width: '100%', marginTop: '20px' }} onClick={() => window.print()}>
            <FaPrint style={{ marginRight: '8px' }} /> Print Bill
          </button>
        </div>
      </div>

      <div className="info-box">
        <h4>Use Cases</h4>
        <ul>
          <li>Grocery and kirana store bills</li>
          <li>General retail or provision shop bills</li>
          <li>Bread, bakery, and snack counter billing</li>
          <li>Wholesale and bulk order billing for shop owners</li>
          <li>Quick printable bills for small shop owners</li>
        </ul>
      </div>
    </div>
  );
};

export default ShopBillGenerator;